import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { PLAY_STORE_URL } from '../../constants';
import { ROUTES } from '../../constants/routes';
import Button from '../../components/ui/Button';
import SEO from '../../components/common/SEO';
import screenOneImg from '../../assets/images/screen_one.png';
import { ChurchIcon, TrophyIcon, BooksIcon, UsersIcon, LightningIcon } from '../../components/common/Icons';

// Interactive Sample Questions for the mini teaser
const SAMPLE_QUESTIONS = [
  {
    question: 'According to Psalm 119:105, what is God’s Word described as?',
    options: [
      'A hammer that breaks rocks',
      'A lamp to my feet and a light to my path',
      'A crown of pure gold',
      'A shield and fortress',
    ],
    correctIndex: 1,
    reference: 'Psalm 119:105',
    insight: '"Your word is a lamp to my feet and a light to my path." God\'s Word provides daily guidance in a dark world.'
  },
  {
    question: 'In 2 Timothy 4:3, what did Paul warn that people in later times would turn away from?',
    options: [
      'Sound doctrine',
      'Roman citizenship',
      'Synagogue traditions',
      'Greek philosophy',
    ],
    correctIndex: 0,
    reference: '2 Timothy 4:3',
    insight: 'Paul urges believers to remain grounded in sound biblical doctrine to avoid falling for false teachings.'
  },
  {
    question: 'How did the Bereans in Acts 17:11 verify the truth of Paul’s preaching?',
    options: [
      'They voted among themselves',
      'They examined the Scriptures daily',
      'They consulted secular philosophers',
      'They asked the city magistrates',
    ],
    correctIndex: 1,
    reference: 'Acts 17:11',
    insight: '"They received the word with all readiness, and searched the Scriptures daily to find out whether these things were so."'
  }
];

const HOW_IT_WORKS_STEPS = [
  {
    step: 'STEP 01',
    title: 'Join in Seconds',
    desc: 'Get the free Android app on Google Play Store and join a vibrant global community of believers.',
    iconTheme: 'step-coral',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="2" width="14" height="20" rx="3" />
        <path d="M12 18h.01" />
        <path d="M12 6v6" />
        <path d="m9 9 3 3 3-3" />
      </svg>
    )
  },
  {
    step: 'STEP 02',
    title: 'Daily Scripture Challenges',
    desc: 'Answer timed multiple-choice questions curated from Old and New Testament passages with full verse references.',
    iconTheme: 'step-ochre',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        <path d="M17 7v4" />
        <path d="M15 9h4" />
      </svg>
    )
  },
  {
    step: 'STEP 03',
    title: 'Grow & Win Real Prizes',
    desc: 'Climb local church and global leaderboards. Students and youth win sponsored Study Bibles, trophies, and awards.',
    iconTheme: 'step-gold',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 4h16v6a6 6 0 0 1-12 0V4z" />
        <path d="M12 16v4" />
        <path d="M8 20h8" />
      </svg>
    )
  }
];

const CORE_FEATURES = [
  {
    category: 'HABIT FORMATION',
    title: 'Daily Scripture Habits',
    desc: 'Build consistency with bite-sized daily challenges that turn passive reading into active, enjoyable recall.',
    theme: 'feature-coral',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <path d="m9 16 2 2 4-4" />
      </svg>
    )
  },
  {
    category: 'BIBLICAL TRUTH',
    title: 'Discern Sound Doctrine',
    desc: 'Deepen your biblical literacy so you are firmly rooted in sound doctrine and prepared to stand strong in truth.',
    theme: 'feature-blue',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M12 8v6" />
        <path d="M9.5 10.5h5" />
      </svg>
    )
  },
  {
    category: 'YOUTH OUTREACH',
    title: 'Youth Leagues & Rewards',
    desc: 'Dedicated tournaments for youth ministries and Sunday schools with real prizes funded by generous partners.',
    theme: 'feature-gold',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="6" />
        <path d="m15.4 12.8 2.6 8.2-6-3-6 3 2.6-8.2" />
        <polygon points="12 5 13.2 7.4 15.8 7.8 13.9 9.6 14.3 12.2 12 11 9.7 12.2 10.1 9.6 8.2 7.8 10.8 7.4" fill="currentColor" stroke="none" />
      </svg>
    )
  },
  {
    category: 'CHURCH LEAGUES',
    title: 'Host Church & Group Leagues',
    desc: 'Empower your Sunday school or congregation with custom quizzes, sermon series challenges, private leaderboards, and member chat.',
    theme: 'feature-ochre',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2v3" />
        <path d="M10.5 3.5h3" />
        <path d="M12 5 6 9v12h12V9l-6-4z" />
        <path d="M10 21v-4a2 2 0 0 1 4 0v4" />
      </svg>
    )
  },
  {
    category: 'MILESTONES',
    title: 'Celebrate Scripture Milestones',
    desc: 'Track your accuracy across Biblical categories (Gospels, Epistles, Pentateuch, Wisdom, and Prophets).',
    theme: 'feature-purple',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
        <path d="M12 2v3" />
        <path d="M12 19v3" />
        <path d="M2 12h3" />
        <path d="M19 12h3" />
      </svg>
    )
  },
  {
    category: 'COMMUNITY',
    title: 'Dedicated Ministry Care',
    desc: 'Connect directly with our ministry leadership team whenever you need help or guidance setting up leagues.',
    theme: 'feature-rose',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        <path d="M12 11v6" />
        <path d="M9 14h6" />
      </svg>
    )
  }
];

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');

  // Mini Demo Quiz State
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const currentQ = SAMPLE_QUESTIONS[currentQIndex];

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(null);
    setShowExplanation(false);
    setCurrentQIndex((prev) => (prev + 1) % SAMPLE_QUESTIONS.length);
  };

  return (
    <div className="landing-container">
      <SEO
        title="Bible Contest — Transform Daily Scripture Reading into a Joyful Habit"
        description="Play timed Scripture quizzes, compete in church & student leagues, win sponsored study prizes, and build a lasting daily Bible reading habit on Android."
        keywords="Bible contest, Scripture quiz, Christian trivia, daily Bible reading, Sunday school games, Bible competition, church leagues, Bible memorization"
        canonicalUrl="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Bible Contest Official Website",
          "url": "https://biblecontest.app/",
          "description": "Transform daily Scripture reading into a joyful habit. Play timed Scripture quizzes and compete in church leagues.",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Bible Contest",
            "url": "https://biblecontest.app/"
          }
        }}
      />

      {/* ─── 1. HERO SECTION ──────────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge__dot" />
            <span>A Fun, Faith-Building Journey Through God’s Word</span>
          </div>

          <h1 className="hero-title">
            Transform Daily Scripture Reading into a <span className="highlight-text">Joyful Habit</span>
          </h1>

          <p className="hero-subtitle">
            Play timed Scripture quizzes, host custom leagues for your church or youth group, win sponsored prizes, and anchor your faith in God's infallible Word.
          </p>

          <div className="hero-cta-group">
            {/* Primary Action Pair: Web Gameplay + Google Play Badge */}
            <div className="hero-cta-primary-row">
              {/* Show Login to Play on non-Android devices for users who are not logged in */}
              {!isAndroid && !user && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate(ROUTES.AUTH.LOGIN)}
                  className="btn-hero-primary"
                >
                  Login to Play →
                </Button>
              )}

              {!isAndroid && user && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate(ROUTES.GAMES.PLAY)}
                  className="btn-hero-primary"
                >
                  Play Games →
                </Button>
              )}

              {/* Direct Google Play Store Button */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-play-store"
                aria-label="Download Bible Contest on Google Play"
              >
                <svg className="play-store-svg" viewBox="0 0 24 24" width="22" height="22">
                  <path fill="#4285F4" d="M3.61 2.39L13.84 12.62 3.61 22.85c-.41-.33-.61-.84-.61-1.44z"/>
                  <path fill="#FBBC04" d="M17.47 8.99l-3.63 3.63L3.61 2.39c.27-.22.61-.39 1.01-.39.52 0 1.03.17 1.48.42l11.37 6.57z"/>
                  <path fill="#34A853" d="M17.47 16.25L6.1 22.82c-.45.26-.96.42-1.48.42-.4 0-.74-.17-1.01-.39l10.23-10.23 3.63 3.63z"/>
                  <path fill="#EA4335" d="M21.6 13.84l-4.13 2.41-3.63-3.63 3.63-3.63 4.13 2.41c.88.51.88 1.93 0 2.44z"/>
                </svg>
                <div className="btn-play-store__text">
                  <span className="small">GET IT ON</span>
                  <span className="big">Google Play</span>
                </div>
              </a>
            </div>

            {/* Dedicated Ministry Sub-Ribbon (Church & Youth Group Leaders) */}
            <div className="hero-cta-ministry-ribbon">
              <span className="ministry-ribbon-text">
                <ChurchIcon size={16} />
                <span>Organizing for your church or youth ministry?</span>
              </span>
              <button
                type="button"
                className="ministry-ribbon-btn"
                onClick={() => navigate(user ? ROUTES.PUBLIC.STUDIO : ROUTES.AUTH.LOGIN)}
              >
                Host a League →
              </button>
            </div>
          </div>

          <div className="hero-stats-row">
            <div className="stat-item">
              <strong>1500+</strong>
              <span>Quizzes Played</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <strong>400+</strong>
              <span>Active Learners</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <strong>100%</strong>
              <span>Scripture Grounded</span>
            </div>
          </div>
        </div>

        {/* Hero Visual Mockup */}
        <div className="hero-visual">
          <div className="phone-mockup">
            <img
              src={screenOneImg}
              alt="Bible Contest Mobile App Preview"
              className="phone-screen-img"
            />
          </div>
        </div>
      </section>

      {/* ─── 2. OUR VISION & HEART STATEMENT (ABOUT US) ────────────────────── */}
      <section className="vision-section">
        <div className="vision-card">
          <div className="vision-badge">
            ABOUT OUR MINISTRY
          </div>
          <h2 className="vision-title">Why We Founded Bible Contest</h2>
          <p className="vision-lead">
            We started Bible Contest with a singular conviction: in a generation full of digital distractions, Christians need an engaging, joyful way to build an unbreakable daily habit of reading God’s Word.
          </p>

          <div className="vision-pillars-grid">
            <div className="vision-pillar-item">
              <span className="vision-pillar-num">01</span>
              <h4>Daily Scripture Habit</h4>
              <p>
                Transforming passive reading into active, daily recall through interactive, timed Scripture challenges.
              </p>
            </div>

            <div className="vision-pillar-item">
              <span className="vision-pillar-num">02</span>
              <h4>Sound Biblical Doctrine</h4>
              <p>
                Deepening biblical literacy so believers of every age can discern false teachings and stand firm in truth.
              </p>
            </div>

            <div className="vision-pillar-item">
              <span className="vision-pillar-num">03</span>
              <h4>Host Your Own League</h4>
              <p>
                Empowering pastors, teachers, and youth leaders to host custom quizzes, sermon leagues, and church leaderboards.
              </p>
            </div>

            <div className="vision-pillar-item">
              <span className="vision-pillar-num">04</span>
              <h4>Free for Every Church</h4>
              <p>
                100% ad-free and free for Sunday schools, youth groups, and families worldwide, powered by Kingdom partners.
              </p>
            </div>
          </div>

          <div className="vision-scripture-bar">
            <span className="scripture-bar-label">Scriptural Foundation</span>
            <div className="vision-scripture-pills">
              <span>Psalm 119:105</span>
              <span>2 Timothy 4:3</span>
              <span>Acts 17:11</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 3. INTERACTIVE MINI QUIZ TEASER ───────────────────────────────── */}
      <section className="interactive-teaser-section">
        <div className="teaser-wrapper card">
          <div className="teaser-header">
            <span className="hero-pill">INTERACTIVE TEASER</span>
            <h2>Test Your Bible Knowledge Right Now</h2>
            <p>Experience how instant feedback and scripture insights help you memorize God's Word.</p>
          </div>

          <div className="teaser-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="teaser-q-count">
                Question {currentQIndex + 1} of {SAMPLE_QUESTIONS.length}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--clr-muted)', fontWeight: 600 }}>
                {Math.round(((currentQIndex + 1) / SAMPLE_QUESTIONS.length) * 100)}% Complete
              </span>
            </div>

            {/* Mini Progress Bar */}
            <div style={{ width: '100%', height: '4px', background: 'var(--clr-border)', borderRadius: '2px', margin: '6px 0 16px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${((currentQIndex + 1) / SAMPLE_QUESTIONS.length) * 100}%`,
                  height: '100%',
                  background: 'var(--clr-primary)',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>

            <h3 className="teaser-question-text">{currentQ.question}</h3>

            <div className="teaser-options-grid">
              {currentQ.options.map((opt, idx) => {
                let btnStyle = 'teaser-opt-btn';
                if (showExplanation) {
                  if (idx === currentQ.correctIndex) btnStyle += ' correct';
                  else if (selectedAnswer === idx) btnStyle += ' incorrect';
                }
                return (
                  <button
                    key={idx}
                    type="button"
                    className={btnStyle}
                    onClick={() => handleAnswerSelect(idx)}
                    disabled={showExplanation}
                  >
                    <span className="opt-letter">{String.fromCharCode(65 + idx)}</span>
                    <span className="opt-text" style={{ flex: 1 }}>{opt}</span>
                    {showExplanation && idx === currentQ.correctIndex && <span style={{ color: '#166534', fontWeight: 800 }}>✓</span>}
                    {showExplanation && selectedAnswer === idx && idx !== currentQ.correctIndex && <span style={{ color: '#991B1B', fontWeight: 800 }}>✕</span>}
                  </button>
                );
              })}
            </div>

            {showExplanation && (
              <div className="teaser-explanation-box">
                <div className="explanation-header">
                  <strong>{selectedAnswer === currentQ.correctIndex ? 'Excellent!' : 'Scripture Context:'}</strong>
                  <span style={{ fontWeight: 700, color: 'var(--clr-primary)' }}>{currentQ.reference}</span>
                </div>
                <p>{currentQ.insight}</p>
                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="primary" size="sm" onClick={handleNextQuestion}>
                    Next Sample Question →
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── 4. HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="how-it-works-section">
        <h2 className="section-heading">How Bible Contest Works</h2>
        <p className="section-subheading">Three simple steps to elevate your spiritual journey every day.</p>

        <div className="steps-grid">
          {HOW_IT_WORKS_STEPS.map((item, idx) => (
            <div key={idx} className="step-card card">
              <div className="step-card-header">
                <div className={`step-icon-box ${item.iconTheme}`}>
                  {item.icon}
                </div>
                <span className="step-badge">{item.step}</span>
              </div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 4B. HOST A LEAGUE SPOTLIGHT ─────────────────────────────────── */}
      <section className="host-league-spotlight-section">
        <div className="host-league-card">
          <div className="host-league-header">
            <span className="host-league-pill">
              <ChurchIcon size={14} /> CHURCH &amp; YOUTH LEAGUE HOSTING
            </span>
            <h2 className="host-league-title">
              Host Custom Bible Quiz Leagues for Your Church
            </h2>
            <p className="host-league-subtitle">
              Pastors, Sunday school teachers, youth leaders, and fellowship organizers can easily create and manage custom Scripture competitions tailored to sermon series, VBS camps, or youth group studies.
            </p>
          </div>

          <div className="host-league-grid">
            <div className="host-league-feature-item">
              <div className="host-league-icon-box" style={{ background: 'rgba(255, 92, 86, 0.1)', color: 'var(--clr-primary)' }}>
                <BooksIcon size={22} />
              </div>
              <h4>Custom Quizzes &amp; Series</h4>
              <p>Design your own multi-round seasons or chapter-by-chapter quizzes reinforcing Sunday sermons and study guides.</p>
            </div>

            <div className="host-league-feature-item">
              <div className="host-league-icon-box" style={{ background: 'rgba(5, 150, 105, 0.1)', color: '#059669' }}>
                <TrophyIcon size={22} />
              </div>
              <h4>Private Church Leaderboards</h4>
              <p>Track congregation participation and friendly standings with automated real-time score tracking and awards.</p>
            </div>

            <div className="host-league-feature-item">
              <div className="host-league-icon-box" style={{ background: 'rgba(37, 99, 235, 0.1)', color: '#2563EB' }}>
                <UsersIcon size={22} />
              </div>
              <h4>Engage &amp; Chat with Members</h4>
              <p>Encourage fellowship with built-in season chat, prayer sharing, and weekly scripture reflection among participants.</p>
            </div>

            <div className="host-league-feature-item">
              <div className="host-league-icon-box" style={{ background: 'rgba(217, 119, 6, 0.1)', color: '#D97706' }}>
                <LightningIcon size={22} />
              </div>
              <h4>Instant Web &amp; App Access</h4>
              <p>Members can join in seconds using simple league invite codes on both web browsers and our free Android app.</p>
            </div>
          </div>

          <div className="host-league-footer">
            <div className="host-league-cta-box">
              <div className="host-league-cta-text">
                <strong>Ready to host a league for your congregation?</strong>
                <span>It takes under two minutes to launch your first season. 100% free and ad-free forever.</span>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate(user ? ROUTES.PUBLIC.STUDIO : ROUTES.AUTH.LOGIN)}
                style={{ minHeight: '48px', borderRadius: '12px', fontWeight: 700 }}
              >
                Launch Your League Now →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 5. CORE FEATURES ─────────────────────────────────────────────── */}
      <section className="features-section">
        <h2 className="section-heading">Built for Spiritual Growth &amp; Fellowship</h2>
        <p className="section-subheading">Every feature is designed to anchor your mind in truth and strengthen the body of Christ.</p>
        <div className="features-grid">
          {CORE_FEATURES.map((feat, idx) => (
            <div key={idx} className={`feature-item card ${feat.theme}`}>
              <div className="feature-item-top">
                <div className="feature-icon-box">
                  {feat.icon}
                </div>
                <span className="feature-category-badge">{feat.category}</span>
              </div>
              <h3>{feat.title}</h3>
              <p>{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 6. MINISTRY CALLOUTS (VOLUNTEER, DONATE, BLOG) ───────────────── */}
      <section className="ministry-pillars-section">
        <div className="ministry-pillar-card card volunteer-theme">
          <div className="ministry-pillar-topbar">
            <span className="ministry-tag volunteer">SERVE WITH US</span>
          </div>
          <h3>Share Your God-Given Gifts</h3>
          <p>
            Help write Scripture questions, review theological accuracy, translate quizzes into regional languages, or build software with us.
          </p>
          <Button variant="secondary" onClick={() => navigate(ROUTES.PUBLIC.VOLUNTEER)}>
            View Opportunities →
          </Button>
        </div>

        <div className="ministry-pillar-card card donate-theme">
          <div className="ministry-pillar-topbar">
            <span className="ministry-tag donate">SUPPORT MISSION</span>
          </div>
          <h3>Partner with Us</h3>
          <p>
            Join as a Kingdom Partner to power real-time live quizzes, crown young scholars with Study Bibles, and keep Scripture free worldwide.
          </p>
          <Button variant="primary" onClick={() => navigate(ROUTES.PUBLIC.DONATE)}>
            Become a Partner →
          </Button>
        </div>

        <div className="ministry-pillar-card card host-theme">
          <div className="ministry-pillar-topbar">
            <span className="ministry-tag host">HOST A LEAGUE</span>
          </div>
          <h3>Empower Your Congregation</h3>
          <p>
            Host custom Scripture competitions for your Sunday school, youth ministry, or home church with our dedicated Host Studio.
          </p>
          <Button variant="secondary" onClick={() => navigate(user ? ROUTES.PUBLIC.STUDIO : ROUTES.AUTH.LOGIN)}>
            Host a League →
          </Button>
        </div>

        <div className="ministry-pillar-card card blog-theme">
          <div className="ministry-pillar-topbar">
            <span className="ministry-tag blog">SCRIPTURE INSIGHTS</span>
          </div>
          <h3>Biblical Insights Blog</h3>
          <p>
            Read uplifting articles on building daily Bible study habits, student winner spotlights, and contest updates.
          </p>
          <Button variant="ghost" onClick={() => navigate(ROUTES.PUBLIC.BLOG)}>
            Read Articles →
          </Button>
        </div>
      </section>

      {/* ─── 7. FINAL DOWNLOAD CTA BANNER ─────────────────────────────────── */}
      <section className="final-cta-section">
        <div className="final-cta-card">
          <h2>Ready to Test Your Knowledge or Host a League?</h2>
          <p>
            Join thousands of Christians studying and growing in God's Word every day, or host a custom quiz league for your church.
          </p>
          <div className="final-cta-wrapper">
            <div className="final-cta-primary-row">
              {!isAndroid && !user && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate(ROUTES.AUTH.LOGIN)}
                  className="btn-hero-primary"
                >
                  Login to Play →
                </Button>
              )}

              {!isAndroid && user && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate(ROUTES.GAMES.PLAY)}
                  className="btn-hero-primary"
                >
                  Play Games →
                </Button>
              )}

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-play-store"
                aria-label="Download Bible Contest on Google Play"
              >
                <svg className="play-store-svg" viewBox="0 0 24 24" width="22" height="22">
                  <path fill="#4285F4" d="M3.61 2.39L13.84 12.62 3.61 22.85c-.41-.33-.61-.84-.61-1.44V3.83c0-.6.2-1.11.61-1.44z"/>
                  <path fill="#FBBC04" d="M17.47 8.99l-3.63 3.63L3.61 2.39c.27-.22.61-.39 1.01-.39.52 0 1.03.17 1.48.42l11.37 6.57z"/>
                  <path fill="#34A853" d="M17.47 16.25L6.1 22.82c-.45.26-.96.42-1.48.42-.4 0-.74-.17-1.01-.39l10.23-10.23 3.63 3.63z"/>
                  <path fill="#EA4335" d="M21.6 13.84l-4.13 2.41-3.63-3.63 3.63-3.63 4.13 2.41c.88.51.88 1.93 0 2.44z"/>
                </svg>
                <div className="btn-play-store__text">
                  <span className="small">DOWNLOAD ON</span>
                  <span className="big">Google Play Store</span>
                </div>
              </a>
            </div>

            <div className="hero-cta-ministry-ribbon final-ministry-ribbon">
              <span className="ministry-ribbon-text">
                <ChurchIcon size={16} />
                <span>Ready to engage your congregation?</span>
              </span>
              <button
                type="button"
                className="ministry-ribbon-btn"
                onClick={() => navigate(user ? ROUTES.PUBLIC.STUDIO : ROUTES.AUTH.LOGIN)}
              >
                Host a League →
              </button>
            </div>
          </div>

          <div className="cta-trust-badges">
            <span>100% Free Forever</span>
            <span className="cta-trust-dot">•</span>
            <span>Verified Scripture</span>
            <span className="cta-trust-dot">•</span>
            <span>Zero In-App Ads</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
